// RepoSwitcher — top-bar picker for the active repository within a workspace.
// Replaces the former workspace switcher (workspaces now live in the BrandMenu).
// Reads :slug and :repo from the URL; lists the workspace's repos and jumps to
// the editor of whichever repo is picked.

import { useNavigate, useParams } from "react-router-dom";
import { useContextMenu } from "../ui/ContextMenu";
import { Menu, MenuButton, MenuItems, MenuItem } from "../ui/Menu";
import { useRepos, type Repo } from "../repos/useRepos";
import { cx } from "../lib/classNames";
import { repoHome, repoHistory, wsHome } from "../lib/paths";
import { useTranslation } from "../i18n";

function RepoIcon({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4.5A2.5 2.5 0 016.5 2H20v15H6.5A2.5 2.5 0 004 19.5V4.5zM20 17v4H6.5A2.5 2.5 0 014 18.5" />
    </svg>
  );
}

export function RepoSwitcher() {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const menu = useContextMenu();
  const { slug = "", repo: activeRepo } = useParams<{ slug?: string; repo?: string }>();
  const repos = useRepos(slug);

  if (!slug) return null;

  const items: readonly Repo[] = repos.data?.repos ?? [];
  const ordered = [...items].sort((a, b) => a.slug.localeCompare(b.slug));
  const active = ordered.find((r) => r.slug === activeRepo);

  function openRepo(r: Repo) {
    void navigate(repoHome(slug, r.slug));
  }

  function repoMenuItems(r: Repo) {
    // Clone URL mirrors the browser URL — copy the address bar and clone.
    const cloneUrl = window.location.origin + repoHome(slug, r.slug);
    return [
      {
        label: t("repoSwitcher.copyCloneUrl"),
        onClick: () => void navigator.clipboard.writeText(cloneUrl),
      },
      {
        label: t("repoSwitcher.history"),
        onClick: () => void navigate(repoHistory(slug, r.slug, r.defaultBranch)),
      },
    ];
  }

  return (
    <>
    <Menu placement="bottom-start">
      <MenuButton className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-slate-100 transition-colors hover:bg-slate-800/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arch-400">
        <RepoIcon className="h-4 w-4 shrink-0 text-slate-500" />
        <span className="max-w-48 truncate">
          {active ? active.slug : t("repoSwitcher.select")}
        </span>
        <svg className="shrink-0 text-slate-500" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </MenuButton>

      <MenuItems className="z-overlay min-w-60 rounded-xl border border-slate-800 bg-slate-900/95 p-1.5 shadow-xl shadow-black/40 backdrop-blur">
        <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
          {t("repoSwitcher.heading")}
        </p>

        {ordered.length === 0 ? (
          <p className="px-2 py-2 text-sm text-slate-500">{t("repoSwitcher.empty")}</p>
        ) : (
          ordered.map((r) => (
            <MenuItem
              key={r.id}
              onClick={() => openRepo(r)}
              className={cx(
                "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none",
                r.slug === activeRepo ? "text-slate-100" : "text-slate-300",
              )}
            >
              <RepoIcon className={cx("h-4 w-4 shrink-0", r.slug === activeRepo ? "text-arch-400" : "text-slate-500")} />
              <span
                className="flex-1 truncate text-left"
                onContextMenu={(e) => menu.open(e, repoMenuItems(r))}
              >
                {r.slug}
              </span>
              {r.slug === activeRepo ? (
                <svg className="h-4 w-4 shrink-0 text-arch-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : null}
            </MenuItem>
          ))
        )}

        {active ? (
          <>
            <div className="my-1 border-t border-slate-800" />
            <MenuItem
              onClick={() => void navigate(repoHistory(slug, active.slug, active.defaultBranch))}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none"
            >
              <svg className="h-4 w-4 shrink-0 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 8v4l3 2M3 12a9 9 0 1018 0 9 9 0 00-18 0z" />
              </svg>
              {t("repoSwitcher.history")}
            </MenuItem>
          </>
        ) : null}

        <div className="my-1 border-t border-slate-800" />
        <MenuItem
          onClick={() => void navigate(wsHome(slug))}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 focus-visible:bg-slate-800 focus-visible:outline-none"
        >
          <svg className="h-4 w-4 shrink-0 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {t("repoSwitcher.allRepositories")}
        </MenuItem>
      </MenuItems>
    </Menu>
    {menu.node}
    </>
  );
}
